import { useCallback } from 'react'
import { isAddress } from '@components/Web3/Wallet/useGetWalletParam'
import { getAbstractAccountAddress } from 'hooks/useAbstractAccountAddress'
import { fetchMembershipStatus, useBaseReads, useBaseWrites } from 'use-towns-client'

const retry = async <T>(
    fn: () => Promise<T>,
    options?: {
        maxRetries?: number
        delayMs?: number
        validator?: (result: T) => boolean
        retryErrorMessage?: string
    },
): Promise<T> => {
    const {
        maxRetries = 3,
        delayMs = 1000,
        validator,
        retryErrorMessage = 'Retry validation failed',
    } = options ?? {}
    let attempt = 0
    let lastError: Error = new Error('Unknown error')

    while (attempt < maxRetries) {
        try {
            const result = await fn()
            if (!validator || validator(result)) {
                return result
            }
            lastError = new Error(retryErrorMessage)
        } catch (error) {
            lastError = error instanceof Error ? error : new Error(String(error))
        }

        attempt++
        if (attempt < maxRetries) {
            await new Promise((resolve) => setTimeout(resolve, delayMs * attempt))
        }
    }
    throw lastError
}

/**
 * Fetch my user's membership status on request - only considers the smart account address
 * @param args - The arguments for the hook
 * @returns The membership status
 */
export function useFetchMyMembershipStatusAsync(args: { spaceId: string | undefined }) {
    const { getMembershipStatus } = useBaseReads()
    const { userOpsInstance } = useBaseWrites()

    return useCallback(
        async ({ userId }: { userId: string }) => {
            if (!args.spaceId) {
                throw new Error('spaceId is required')
            }
            if (!isAddress(userId)) {
                throw new Error('userId is not a valid address')
            }
            const smartAccountAddress = await getAbstractAccountAddress({
                rootKeyAddress: userId,
                isMyAbstractAccount: true,
                isAccountAbstractionEnabled: true,
                userOpsInstance,
            })
            if (!smartAccountAddress) {
                throw new Error('smartAccountAddress is not a valid address')
            }
            const { spaceId } = args

            try {
                const result = await retry(
                    () =>
                        fetchMembershipStatus({
                            spaceId,
                            userId,
                            getMembershipStatus,
                            wallets: [smartAccountAddress],
                        }),
                    {
                        validator: (result) => !!result.tokenId,
                        retryErrorMessage:
                            'useFetchMembershipStatusAsync::fetchMembershipStatus returned no tokenId',
                    },
                )
                console.log('useFetchMembershipStatusAsync::result', result)
                return result
            } catch (error) {
                console.error(
                    'useFetchMembershipStatusAsync::Error fetching membership status',
                    error,
                )
                throw error
            }
        },
        [args, getMembershipStatus, userOpsInstance],
    )
}
